import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, ChevronRight, User, MapPin, Plane, Shield, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: 'basics', label: 'Basics', icon: User },
  { key: 'address', label: 'Address', icon: MapPin },
  { key: 'travel', label: 'Travel', icon: Plane },
  { key: 'emergency', label: 'Emergency', icon: Shield },
  { key: 'preferences', label: 'Preferences', icon: Heart },
];

const STATUS_OPTIONS = ['lead', 'active', 'vip', 'inactive', 'archived'];

function SectionNav({ active, onChange }) {
  return (
    <div className="flex gap-1 overflow-x-auto pb-2 border-b border-[var(--vt-border)] mb-4">
      {SECTIONS.map(s => {
        const Icon = s.icon;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${
              active === s.key
                ? 'bg-[var(--vt-gold)]/10 text-[var(--vt-gold)] font-medium'
                : 'text-[var(--vt-text-muted)] hover:bg-[var(--vt-bg-muted)]'
            }`}
            data-testid={`section-tab-${s.key}`}
          >
            <Icon className="w-3 h-3" />
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

const EMPTY_FORM = {
  full_name: '', email: '', phone: '', status: 'lead', source: '', target_budget: 0, notes: '',
  address: '', street_address: '', city: '', state_province: '', postal_code: '', country: '',
  home_airport: '', airline_preferences: '', seat_preference: '',
  dietary_needs: '', allergies: '', travel_pace: '', budget_range: '', travel_preferences: '',
  passport_number: '', passport_issuing_country: '', passport_expiration_date: '', nationality: '',
  known_traveler_number: '', redress_number: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  celebration_dates: '',
};

export function ClientFormModal({ open, onClose, onSaved, client = null }) {
  const isEdit = !!client;
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState('basics');
  const [form, setForm] = useState({ ...EMPTY_FORM });

  useEffect(() => {
    if (open) {
      if (client) {
        setForm({ ...EMPTY_FORM, ...client });
      } else {
        setForm({ ...EMPTY_FORM });
      }
      setSection('basics');
    }
  }, [client, open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      toast.error('Client name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/clients/${client.id}`, form);
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/clients`, form);
        toast.success('Client created');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const fieldProps = (field, opts = {}) => ({
    value: form[field] ?? '',
    onChange: e => handleChange(field, opts.number ? (parseFloat(e.target.value) || 0) : e.target.value),
    className: 'bg-[var(--vt-input-bg)] border-[var(--vt-input-border)] text-sm h-9',
    'data-testid': `client-${field}-input`,
    ...opts,
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
        <DialogHeader>
          <DialogTitle className="font-serif text-[var(--vt-text)]">
            {isEdit ? 'Edit Client' : 'New Client'}
          </DialogTitle>
        </DialogHeader>

        <SectionNav active={section} onChange={setSection} />

        {/* ===== BASICS ===== */}
        {section === 'basics' && (
          <div className="space-y-3" data-testid="section-basics">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Full Name *</Label>
              <Input {...fieldProps('full_name')} placeholder="Household / Entity Name" data-testid="client-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Email</Label>
                <Input type="email" {...fieldProps('email')} data-testid="client-email-input" />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Phone</Label>
                <Input {...fieldProps('phone')} data-testid="client-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Status</Label>
                <select
                  value={form.status || 'lead'}
                  onChange={e => handleChange('status', e.target.value)}
                  className="w-full h-9 rounded-md border bg-[var(--vt-input-bg)] border-[var(--vt-input-border)] px-3 text-sm"
                  data-testid="client-status-input"
                >
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Referral Source</Label>
                <Input {...fieldProps('source')} placeholder="e.g. Referral, Instagram" data-testid="client-source-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Target Budget</Label>
                <Input type="number" step="100" {...fieldProps('target_budget', { number: true })} data-testid="client-budget-input" />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Celebration Dates</Label>
                <Input {...fieldProps('celebration_dates')} placeholder="Anniversary: Jun 15" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Notes</Label>
              <Textarea {...fieldProps('notes')} rows={2} className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)] text-sm" data-testid="client-notes-input" />
            </div>
          </div>
        )}

        {/* ===== ADDRESS ===== */}
        {section === 'address' && (
          <div className="space-y-3" data-testid="section-address">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Street Address</Label>
              <Input {...fieldProps('street_address')} data-testid="client-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">City</Label>
                <Input {...fieldProps('city')} />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">State / Province</Label>
                <Input {...fieldProps('state_province')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Postal Code</Label>
                <Input {...fieldProps('postal_code')} />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Country</Label>
                <Input {...fieldProps('country')} />
              </div>
            </div>
          </div>
        )}

        {/* ===== TRAVEL ===== */}
        {section === 'travel' && (
          <div className="space-y-3" data-testid="section-travel">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Home Airport</Label>
                <Input {...fieldProps('home_airport')} placeholder="e.g. JFK" />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Seat Preference</Label>
                <Input {...fieldProps('seat_preference')} placeholder="Window, Aisle" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Airline Preferences</Label>
              <Input {...fieldProps('airline_preferences')} placeholder="e.g. Delta, United" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Passport Number</Label>
                <Input {...fieldProps('passport_number')} />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Passport Expiration</Label>
                <Input type="date" {...fieldProps('passport_expiration_date')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Issuing Country</Label>
                <Input {...fieldProps('passport_issuing_country')} />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Nationality</Label>
                <Input {...fieldProps('nationality')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Known Traveler # (KTN)</Label>
                <Input {...fieldProps('known_traveler_number')} />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Redress Number</Label>
                <Input {...fieldProps('redress_number')} />
              </div>
            </div>
          </div>
        )}

        {/* ===== EMERGENCY ===== */}
        {section === 'emergency' && (
          <div className="space-y-3" data-testid="section-emergency">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Emergency Contact Name</Label>
              <Input {...fieldProps('emergency_contact_name')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Phone</Label>
                <Input {...fieldProps('emergency_contact_phone')} />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Relationship</Label>
                <Input {...fieldProps('emergency_contact_relationship')} placeholder="e.g. Spouse, Parent" />
              </div>
            </div>
          </div>
        )}

        {/* ===== PREFERENCES ===== */}
        {section === 'preferences' && (
          <div className="space-y-3" data-testid="section-preferences">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Dietary Needs</Label>
                <Input {...fieldProps('dietary_needs')} placeholder="Vegetarian, Gluten-free" />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Allergies</Label>
                <Input {...fieldProps('allergies')} placeholder="Nuts, Shellfish" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Travel Pace</Label>
                <Input {...fieldProps('travel_pace')} placeholder="Relaxed, Active, Mixed" />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Budget Range</Label>
                <Input {...fieldProps('budget_range')} placeholder="Luxury, Mid-range" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Travel Preferences</Label>
              <Textarea {...fieldProps('travel_preferences')} rows={3} placeholder="Prefers boutique hotels, cultural experiences..." className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)] text-sm" />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--vt-border)]">
          <Button variant="ghost" onClick={onClose} data-testid="client-cancel-btn">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
            data-testid="client-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Update Client' : 'Create Client'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
